import styled from 'styled-components'
import { screenSizes, type Surface } from '@shipt/design-system-themes'
import { spacingPx } from '@/features/shared/theme/tokens'
import { Image } from '@/features/shared/elements/Image/NextImage'
import {
  type SplitSectionData,
  type AstroButton,
  type AppDownloadButtons,
  type SplitSectionBlock as SplitSectionBlockType,
} from '@/features/cms/components/CMS/types'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import { Body, Headline } from '@shipt/design-system-typography'
import { renderElement, renderElementInverse } from '@/features/cms/components/CMS/RichTextRenderer'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { Column, Row } from '@shipt/design-system-layouts'
import { ButtonLink } from '@/features/shared/elements/Link/Buttons'
import { AppBadges } from '@/features/shared/components/AppBadges'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { AccordionGroup } from '@shipt/design-system-accordion'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { useDeriveColorProps } from '@/features/cms/components/CMS/hooks/useDeriveColorProps'

const layoutRatioConfig = {
  '50/50': [
    { $start: 1, $span: 6 },
    { $start: 7, $span: 6 },
  ],
  '33/66': [
    { $start: 1, $span: 4 },
    { $start: 5, $span: 8 },
  ],
  '66/33': [
    { $start: 1, $span: 8 },
    { $start: 9, $span: 4 },
  ],
  '25/75': [
    { $start: 1, $span: 3 },
    { $start: 4, $span: 9 },
  ],
  '75/25': [
    { $start: 1, $span: 9 },
    { $start: 10, $span: 3 },
  ],
} as const

/**
 * This component corresponds to the `split_section` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/split_section
 * - **Production** - https://cms.shipt.com/content-type/pool/split_section
 */
export const SplitSection = ({
  content_type_id,
  id,
  data,
  className,
}: SplitSectionData & { className?: string }) => {
  const {
    sections,
    section_size,
    background_color,
    section_padding,
    section_padding_tablet,
    align = 'center',
    mobile_layout = 'stacked',
  } = data

  const { color: sectionBackgroundColor, surface } = useDeriveColorProps(
    background_color ?? 'neutral-default',
  )

  try {
    return (
      <ExtendedGLC className={className} $background_color={sectionBackgroundColor}>
        <LayoutGrid
          {...getCMSWrapperProps({ id, content_type_id })}
          spacing="sm"
          padding={{
            base: [section_padding?.[0] ?? 0, 0, section_padding?.[1] ?? section_padding?.[0] ?? 0],
            md: [section_padding_tablet?.[0] ?? 0, 0],
          }}
          align={align}
        >
          <SplitSectionLayoutBlock
            data-mlayout={mobile_layout}
            {...layoutRatioConfig[section_size][0]}
          >
            <SplitSectionBlockContent {...sections[0]} id={id} surface={surface} />
          </SplitSectionLayoutBlock>
          <SplitSectionLayoutBlock
            data-mlayout={mobile_layout}
            {...layoutRatioConfig[section_size][1]}
          >
            <SplitSectionBlockContent {...sections[1]} id={id} surface={surface} />
          </SplitSectionLayoutBlock>
        </LayoutGrid>
      </ExtendedGLC>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

const SplitSectionBlockContent = (props: SplitSectionBlockType & { surface: Surface }) => {
  const { content_type_id, id } = props
  const { trackCMSElementClicked, trackCMSElementViewed } = useCMSTrackingEvents()

  try {
    switch (content_type_id) {
      case 'split_section__image_block': {
        const { image } = props.data
        const { alt, height, width, src } = image
        return <ImageBlock alt={alt} height={height} width={width} src={src} />
      }
      case 'split_section__content_block': {
        const { eyebrow, heading, subheading, trailing_elements } = props.data
        return (
          <Column className="contentBlock" spacing="xl">
            <ScrollIntoView
              onInView={() => {
                trackCMSElementViewed({ content: props.data.heading })
              }}
            >
              <Column spacing={eyebrow ? undefined : 'xl'}>
                {!!eyebrow && (
                  <Headline surface={props.surface} uppercase variant="secondary" as="span">
                    {eyebrow}
                  </Headline>
                )}
                <Headline size="xxl" weight="medium" as="h2" surface={props.surface}>
                  {heading}
                </Headline>
                {!!subheading && (
                  <Body size="xxl" surface={props.surface} as="span">
                    {subheading.elements.map(renderElement)}
                  </Body>
                )}
              </Column>
            </ScrollIntoView>
            {trailing_elements?.map((element) => (
              <SplitSectionContentBlockTrailingElements
                surface={props.surface}
                {...element}
                key={`${content_type_id}-${element.id}`}
              />
            ))}
          </Column>
        )
      }
      case 'split_section__accordion_block': {
        const { accordion_sections } = props.data
        return (
          <Column paddingTop="xl">
            <AccordionGroup>
              {accordion_sections.map((section) => {
                const { data } = section
                return (
                  <AccordionGroup.AccordionItem
                    key={data.heading}
                    title={data.heading}
                    headerAs="h3"
                    shouldPersistContent
                    surface={props.surface}
                    onClick={() =>
                      trackCMSElementClicked({
                        type: 'button',
                        content: data.heading,
                      })
                    }
                  >
                    {props.surface === 'default'
                      ? data.content.elements.map(renderElement)
                      : data.content.elements.map(renderElementInverse)}
                  </AccordionGroup.AccordionItem>
                )
              })}
            </AccordionGroup>
          </Column>
        )
      }
      default:
        throw new Error('SplitSection Content Block Trailing Element type not supported')
    }
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

type SplitSectionContentBlockTrailingElementsProps = AppDownloadButtons | AstroButton

const SplitSectionContentBlockTrailingElements = (
  props: SplitSectionContentBlockTrailingElementsProps & { surface: Surface },
) => {
  const { content_type_id, id, surface } = props
  const { trackCMSElementClicked } = useCMSTrackingEvents()
  const handleButtonClick = (text: string) => () => {
    trackCMSElementClicked({
      content: text,
      type: 'button',
    })
  }

  try {
    switch (content_type_id) {
      case 'astro_button': {
        const { data } = props
        return (
          <CallToAction
            href={data.link?.url ?? ''}
            onClick={handleButtonClick(data.text)}
            opensInNewTab={data.link?.open_in_new_tab}
            surface={surface}
            variant={data.variant}
            size={{ base: 'md', sm: 'lg' }}
          >
            {data.text}
          </CallToAction>
        )
      }

      case 'app_download_buttons': {
        return <AppBadges openInNewTab isShopper={false} />
      }

      default:
        throw new Error('Content type not supported')
    }
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

// This extension should only be needed temporarily until we finish migrating instances of the CMSContainer and widen the site.
const ExtendedGLC = styled(GridLayoutContainer)<{
  $background_color?: string
}>`
  --content-max-width: 1280px;
  width: 100vw;
  background-color: ${({ $background_color }) => $background_color};
`

// Let's reuse this pattern in other head components to align items to a consistent grid
const LayoutGrid = styled(Row)`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
`

const SplitSectionLayoutBlock = styled.div<{
  'data-mlayout': NonNullable<SplitSectionData['data']['mobile_layout']>
  $start: number
  $span: number
}>`
  grid-column: 1 / span 12;

  &[data-mlayout='inline'] {
    grid-column: ${({ $start, $span }) => `${$start} / span ${$span}`};
  }

  /* Proposal, may need to be solved another way if images aren't always on top on mobile */
  &:not(:first-child):has(.contentBlock) {
    order: -1;
  }

  @media ${screenSizes.tablet} {
    grid-column: ${({ $start, $span }) => `${$start} / span ${$span}`};

    &:not(:first-child):has(.contentBlock) {
      order: unset;
    }

    /* Proposal, add margin to the content block based on where it's placed, hopefully we can keep this static and not configurable via CMS */
    &:first-child .contentBlock {
      margin-right: ${spacingPx('xxl')};
    }

    &:not(:first-child) .contentBlock {
      margin-left: ${spacingPx('xxl')};
    }
  }
`
const CallToAction = styled(ButtonLink)`
  align-self: start;
`

const ImageBlock = styled(Image)`
  /* ⬇️ Essential, removes unwanted space below image */
  display: block;
  width: 100%;

  /* ⬇️ Allows image to maintain intrinsic aspect ratio */
  height: auto;
`
